import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { Recipe, Ingredient, RecipesState } from '../../types/recipe';
import { loadRecipesFromStorage, STORAGE_KEYS } from '../../utils/localStorage';
import { parseFractionToDecimal } from '../../utils/fractionParser';
import { componentApiService } from '../../services/componentApiService';
import { fillingApiService } from '../../services/fillingApiService';

// Initial state
const initialState: RecipesState = {
  fillings: [],
  fillingsLoading: false,
  fillingsError: null,
  components: [],
  componentsLoading: false,
  componentsError: null,
};

// Helper function to create a new ingredient
export function createIngredient(
  name: string = '',
  amount: number | string = 0,
  unit: string = '',
  type: 'ingredient' | 'component' = 'ingredient',
  componentId?: string
): Ingredient {
  const amountDisplay = typeof amount === 'string' ? amount : String(amount || '');
  const amountNumeric = typeof amount === 'string' ? parseFractionToDecimal(amount) : amount || 0;

  const ingredient: Ingredient = {
    id: uuidv4(),
    name,
    amount: amountNumeric,
    amountDisplay,
    unit,
    type
  };

  if (componentId) {
    ingredient.componentId = componentId;
  }

  return ingredient;
}

// Helper function to create a blank recipe
export function createBlankRecipe(name: string = 'Recipe Name'): Recipe {
  return {
    id: uuidv4(),
    name,
    ingredients: [],
    yield: 1
  };
}

// Async thunks for fillings

/**
 * Fetch all fillings from the API
 */
export const fetchFillings = createAsyncThunk(
  'recipes/fetchFillings',
  async () => {
    return await fillingApiService.fetchFillings();
  }
);

/**
 * Create a new filling
 */
export const createFillingAsync = createAsyncThunk(
  'recipes/createFilling',
  async (filling: Recipe) => {
    return await fillingApiService.createFilling(filling);
  }
);

/**
 * Update an existing filling
 */
export const updateFillingAsync = createAsyncThunk(
  'recipes/updateFilling',
  async (filling: Recipe) => {
    return await fillingApiService.updateFilling(filling);
  }
);

/**
 * Delete a filling
 */
export const deleteFillingAsync = createAsyncThunk(
  'recipes/deleteFilling',
  async (id: string) => {
    await fillingApiService.deleteFilling(id);
    return id;
  }
);

// Async thunks for components

/**
 * Fetch all components from the API
 */
export const fetchComponents = createAsyncThunk(
  'recipes/fetchComponents',
  async () => {
    return await componentApiService.fetchComponents();
  }
);

/**
 * Create a new component
 */
export const createComponentAsync = createAsyncThunk(
  'recipes/createComponent',
  async (component: Recipe) => {
    return await componentApiService.createComponent(component);
  }
);

/**
 * Update an existing component
 */
export const updateComponentAsync = createAsyncThunk(
  'recipes/updateComponent',
  async (component: Recipe) => {
    return await componentApiService.updateComponent(component);
  }
);

/**
 * Delete a component
 */
export const deleteComponentAsync = createAsyncThunk(
  'recipes/deleteComponent',
  async (id: string) => {
    await componentApiService.deleteComponent(id);
    return id;
  }
);

const recipesSlice = createSlice({
  name: 'recipes',
  initialState,
  reducers: {
    // Fillings actions - DEPRECATED: Use async thunks instead
    // These are kept for backward compatibility during migration
    addFilling: (state, action: PayloadAction<Recipe | undefined>) => {
      const newRecipe = action.payload || createBlankRecipe();
      state.fillings.push(newRecipe);
    },

    updateFilling: (state, action: PayloadAction<Recipe>) => {
      const index = state.fillings.findIndex(r => r.id === action.payload.id);
      if (index !== -1) {
        state.fillings[index] = action.payload;
      }
    },

    deleteFilling: (state, action: PayloadAction<string>) => {
      state.fillings = state.fillings.filter(r => r.id !== action.payload);
    },

    updateFillingName: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const recipe = state.fillings.find(r => r.id === action.payload.id);
      if (recipe) {
        recipe.name = action.payload.name;
      }
    },

    updateFillingYield: (state, action: PayloadAction<{ id: string; yield: number }>) => {
      const recipe = state.fillings.find(r => r.id === action.payload.id);
      if (recipe) {
        recipe.yield = action.payload.yield;
      }
    },

    addFillingIngredient: (state, action: PayloadAction<{ recipeId: string; ingredient: Ingredient }>) => {
      const recipe = state.fillings.find(r => r.id === action.payload.recipeId);
      if (recipe) {
        recipe.ingredients.push(action.payload.ingredient);
      }
    },

    updateFillingIngredient: (state, action: PayloadAction<{ recipeId: string; ingredient: Ingredient }>) => {
      const recipe = state.fillings.find(r => r.id === action.payload.recipeId);
      if (recipe) {
        const index = recipe.ingredients.findIndex(i => i.id === action.payload.ingredient.id);
        if (index !== -1) {
          recipe.ingredients[index] = action.payload.ingredient;
        }
      }
    },

    deleteFillingIngredient: (state, action: PayloadAction<{ recipeId: string; ingredientId: string }>) => {
      const recipe = state.fillings.find(r => r.id === action.payload.recipeId);
      if (recipe) {
        recipe.ingredients = recipe.ingredients.filter(i => i.id !== action.payload.ingredientId);
      }
    },

    setFillingIngredients: (state, action: PayloadAction<{ recipeId: string; ingredients: Ingredient[] }>) => {
      const recipe = state.fillings.find(r => r.id === action.payload.recipeId);
      if (recipe) {
        recipe.ingredients = action.payload.ingredients;
      }
    },

    // Components actions
    addComponent: (state, action: PayloadAction<Recipe | undefined>) => {
      const newRecipe = action.payload || createBlankRecipe();
      state.components.push(newRecipe);
    },

    updateComponent: (state, action: PayloadAction<Recipe>) => {
      const index = state.components.findIndex(r => r.id === action.payload.id);
      if (index !== -1) {
        state.components[index] = action.payload;
      }
    },

    deleteComponent: (state, action: PayloadAction<string>) => {
      state.components = state.components.filter(r => r.id !== action.payload);
    },

    updateComponentName: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const recipe = state.components.find(r => r.id === action.payload.id);
      if (recipe) {
        recipe.name = action.payload.name;
      }
    },

    updateComponentYield: (state, action: PayloadAction<{ id: string; yield: number }>) => {
      const recipe = state.components.find(r => r.id === action.payload.id);
      if (recipe) {
        recipe.yield = action.payload.yield;
      }
    },

    addComponentIngredient: (state, action: PayloadAction<{ recipeId: string; ingredient: Ingredient }>) => {
      const recipe = state.components.find(r => r.id === action.payload.recipeId);
      if (recipe) {
        recipe.ingredients.push(action.payload.ingredient);
      }
    },

    updateComponentIngredient: (state, action: PayloadAction<{ recipeId: string; ingredient: Ingredient }>) => {
      const recipe = state.components.find(r => r.id === action.payload.recipeId);
      if (recipe) {
        const index = recipe.ingredients.findIndex(i => i.id === action.payload.ingredient.id);
        if (index !== -1) {
          recipe.ingredients[index] = action.payload.ingredient;
        }
      }
    },

    deleteComponentIngredient: (state, action: PayloadAction<{ recipeId: string; ingredientId: string }>) => {
      const recipe = state.components.find(r => r.id === action.payload.recipeId);
      if (recipe) {
        recipe.ingredients = recipe.ingredients.filter(i => i.id !== action.payload.ingredientId);
      }
    },

    setComponentIngredients: (state, action: PayloadAction<{ recipeId: string; ingredients: Ingredient[] }>) => {
      const recipe = state.components.find(r => r.id === action.payload.recipeId);
      if (recipe) {
        recipe.ingredients = action.payload.ingredients;
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch fillings
    builder
      .addCase(fetchFillings.pending, (state) => {
        state.fillingsLoading = true;
        state.fillingsError = null;
      })
      .addCase(fetchFillings.fulfilled, (state, action) => {
        state.fillingsLoading = false;
        state.fillings = action.payload;
      })
      .addCase(fetchFillings.rejected, (state, action) => {
        state.fillingsLoading = false;
        state.fillingsError = action.error.message || 'Failed to fetch fillings';
      });

    // Create filling
    builder
      .addCase(createFillingAsync.fulfilled, (state, action) => {
        state.fillings.push(action.payload);
      })
      .addCase(createFillingAsync.rejected, (state, action) => {
        state.fillingsError = action.error.message || 'Failed to create filling';
      });

    // Update filling
    builder
      .addCase(updateFillingAsync.fulfilled, (state, action) => {
        const index = state.fillings.findIndex(f => f.id === action.payload.id);
        if (index !== -1) {
          state.fillings[index] = action.payload;
        }
      })
      .addCase(updateFillingAsync.rejected, (state, action) => {
        state.fillingsError = action.error.message || 'Failed to update filling';
      });

    // Delete filling
    builder
      .addCase(deleteFillingAsync.fulfilled, (state, action) => {
        state.fillings = state.fillings.filter(f => f.id !== action.payload);
      })
      .addCase(deleteFillingAsync.rejected, (state, action) => {
        state.fillingsError = action.error.message || 'Failed to delete filling';
      });

    // Fetch components
    builder
      .addCase(fetchComponents.pending, (state) => {
        state.componentsLoading = true;
        state.componentsError = null;
      })
      .addCase(fetchComponents.fulfilled, (state, action) => {
        state.componentsLoading = false;
        state.components = action.payload;
      })
      .addCase(fetchComponents.rejected, (state, action) => {
        state.componentsLoading = false;
        state.componentsError = action.error.message || 'Failed to fetch components';
      });

    // Create component
    builder
      .addCase(createComponentAsync.fulfilled, (state, action) => {
        state.components.push(action.payload);
      })
      .addCase(createComponentAsync.rejected, (state, action) => {
        state.componentsError = action.error.message || 'Failed to create component';
      });

    // Update component
    builder
      .addCase(updateComponentAsync.fulfilled, (state, action) => {
        const index = state.components.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.components[index] = action.payload;
        }
      })
      .addCase(updateComponentAsync.rejected, (state, action) => {
        state.componentsError = action.error.message || 'Failed to update component';
      });

    // Delete component
    builder
      .addCase(deleteComponentAsync.fulfilled, (state, action) => {
        state.components = state.components.filter(c => c.id !== action.payload);
      })
      .addCase(deleteComponentAsync.rejected, (state, action) => {
        state.componentsError = action.error.message || 'Failed to delete component';
      });
  }
});

export const {
  addFilling,
  updateFilling,
  deleteFilling,
  updateFillingName,
  updateFillingYield,
  addFillingIngredient,
  updateFillingIngredient,
  deleteFillingIngredient,
  setFillingIngredients,
  addComponent,
  updateComponent,
  deleteComponent,
  updateComponentName,
  updateComponentYield,
  addComponentIngredient,
  updateComponentIngredient,
  deleteComponentIngredient,
  setComponentIngredients
} = recipesSlice.actions;

export default recipesSlice.reducer;
